import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { TableSkeleton } from '../components/shared/LoaderSkeleton';
import Pagination from '../components/shared/Pagination';

// Premium Orders Index - Architectural Harmony with InvoiceShow
function OrdersIndex() {
    const [orders, setOrders] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        total_pages: 1,
        total_count: 0,
        current_page: 1,
        per_page: 25
    });

    useEffect(() => {
        loadOrders();
    }, [search, currentPage]);

    const loadOrders = async () => {
        try {
            setLoading(true);
            const response = await api.getOrders({ q: search, page: currentPage });
            if (response.success) {
                setOrders(Array.isArray(response.data) ? response.data : (response.data.orders || []));
                if (response.meta) {
                    setPagination({
                        total_pages: response.meta.total_pages || 1,
                        total_count: response.meta.total_count || 0,
                        current_page: response.meta.current_page || 1,
                        per_page: response.meta.per_page || 25
                    });
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getStatusBadge = (status: string) => {
        const badges: Record<string, string> = {
            completed: 'bg-emerald-50 text-emerald-700 border-emerald-100',
            shipped: 'bg-blue-50 text-blue-700 border-blue-100',
            processing: 'bg-indigo-50 text-indigo-700 border-indigo-100',
            pending: 'bg-amber-50 text-amber-700 border-amber-100',
            cancelled: 'bg-rose-50 text-rose-700 border-rose-100'
        };
        return badges[status] || 'bg-slate-50 text-slate-700 border-slate-100';
    };

    if (loading && orders.length === 0) return <TableSkeleton />;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-slate-50/30 min-h-screen">
            {/* Header / Actions - Styled like InvoiceShow */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <Link to="/" className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                        </Link>
                        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                            Order <span className="text-indigo-600">Management</span>
                        </h1>
                        <span className="px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-100">
                            {pagination.total_count} Orders
                        </span>
                    </div>
                    <p className="text-sm text-slate-500 ml-10">
                        Orchestrating <span className="font-semibold text-slate-700">customer fulfillment</span> & dispatch logs
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <Link
                        to="/orders/manage"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-all shadow-sm shadow-indigo-100"
                    >
                        <svg className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                        Quick Entry
                    </Link>
                    <Link
                        to="/orders/new"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-black transition-all shadow-sm shadow-slate-200"
                    >
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" /></svg>
                        New Order
                    </Link>
                </div>
            </div>

            {/* Metrics Row - Segmented Layout */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-slate-100 uppercase tracking-widest mb-8">
                <div className="p-5 flex-1 group">
                    <h3 className="text-[10px] font-bold text-slate-400 mb-1">Manifest Volume</h3>
                    <p className="text-xl font-black text-slate-900 font-mono">{pagination.total_count}</p>
                    <p className="text-[9px] font-bold text-slate-400 mt-1">Total Orders Recorded</p>
                </div>
                <div className="p-5 flex-1 group">
                    <h3 className="text-[10px] font-bold text-slate-400 mb-1">Awaiting Fulfillment</h3>
                    <p className="text-xl font-black text-indigo-600 font-mono">{orders.filter(o => o.status === 'pending' || o.status === 'confirmed').length}</p>
                    <p className="text-[9px] font-bold text-slate-400 mt-1">Pending Protocols</p>
                </div>
                <div className="p-5 flex-1 group">
                    <h3 className="text-[10px] font-bold text-slate-400 mb-1">Revenue Aggregation</h3>
                    <p className="text-xl font-black text-emerald-600 font-mono">
                        ₹{orders.reduce((sum, o) => sum + (o.total_amount || 0), 0).toLocaleString()}
                    </p>
                    <p className="text-[9px] font-bold text-slate-400 mt-1">Current Feed Valuation</p>
                </div>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
                <div className="relative group max-w-md">
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search orders by number or customer..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                        className="w-full pl-11 pr-4 py-3 bg-white rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none text-sm font-black text-slate-900 transition-all shadow-sm placeholder:text-slate-400"
                    />
                </div>
            </div>

            {/* Data Table - Refined Style */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mb-8 transition-all hover:shadow-md">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-sm">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-100 uppercase tracking-widest text-[10px] font-black text-slate-400">
                                <th className="px-6 py-4">Order Identity</th>
                                <th className="px-6 py-4">Client Registry</th>
                                <th className="px-6 py-4">Genesis Date</th>
                                <th className="px-6 py-4 text-right">Valuation</th>
                                <th className="px-6 py-4 text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {orders.map((order: any) => (
                                <tr key={order.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="px-6 py-4">
                                        <Link to={`/orders/${order.id}`} className="font-bold text-slate-900 hover:text-indigo-600 transition-colors flex items-center gap-2">
                                            {order.order_number}
                                            <svg className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                                        </Link>
                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-tight mt-0.5">UUID: ORD-{order.id.split('-')[0]}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="font-semibold text-slate-700">{order.customer_name}</div>
                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter mt-0.5 italic">Verified Account</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col gap-0.5">
                                            <div className="font-medium text-slate-600 text-[11px]">
                                                {new Date(order.created_at || order.order_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </div>
                                            <div className="text-[10px] text-slate-400 uppercase font-bold tracking-tighter">
                                                {new Date(order.created_at || order.order_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="font-black text-slate-900 font-mono tracking-tighter">₹{order.total_amount?.toLocaleString()}</div>
                                        <div className="text-[9px] font-black text-slate-300 uppercase tracking-widest mt-0.5">Gross Total</div>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-tighter border ${getStatusBadge(order.status)}`}>
                                            {order.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {orders.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-slate-400 italic text-sm">
                                        No order sequences identified in the registry.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Integration */}
            <div className="flex justify-end pt-2">
                <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.total_pages}
                    totalCount={pagination.total_count}
                    perPage={pagination.per_page}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    );
}

export default OrdersIndex;
